import Head from 'next/head';
import Link from 'next/link';
import Header from '../../components/Header';

export default function BrokenPipePage() {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";
  const serviceName = "Broken Pipe Repair";
  const serviceArea = "Kane County, Geneva, Saint Charles, South Elgin";

  const brokenPipeServices = [
    {
      title: "Emergency Pipe Repair",
      description: "Immediate repair for burst or broken pipes to prevent water damage",
      features: ["Emergency service", "Water damage prevention", "Same-day repairs", "Insurance claim support"]
    },
    {
      title: "Pipe Replacement",
      description: "Complete pipe replacement for severely damaged or corroded pipes",
      features: ["Modern pipe materials", "Minimal property damage", "Long-lasting solutions", "Code compliant work"]
    },
    {
      title: "Leak Detection & Repair",
      description: "Advanced leak detection technology to locate and repair hidden pipe breaks",
      features: ["Electronic leak detection", "Non-invasive methods", "Accurate diagnostics", "Preventive solutions"]
    },
    {
      title: "Pipe Relining",
      description: "Trenchless pipe relining for damaged underground or hard-to-reach pipes",
      features: ["No excavation required", "Cured-in-place pipe lining", "Faster completion", "Cost-effective solution"]
    }
  ];

  const commonCauses = [
    "Freezing temperatures causing pipe expansion",
    "Age and corrosion of older pipes",
    "High water pressure",
    "Tree root intrusion",
    "Ground shifting or settling",
    "Poor installation or materials"
  ];

  const emergencySigns = [
    "Sudden loss of water pressure",
    "Water pooling in yard or basement",
    "Unexplained increase in water bills",
    "Discolored or rusty water",
    "Strange odors from drains",
    "Foundation cracks or settling"
  ];

  const faqItems = [
    {
      question: "How quickly can you respond to a broken pipe emergency?",
      answer: "We provide emergency service for broken pipes throughout Kane County. Our goal is to respond within 1-2 hours to prevent further water damage and begin repairs immediately."
    },
    {
      question: "Will my insurance cover broken pipe repairs?",
      answer: "Many homeowner's insurance policies cover sudden and accidental pipe breaks. We work directly with insurance companies and can help document the damage for your claim."
    },
    {
      question: "How do you locate broken pipes without damaging my property?",
      answer: "We use advanced electronic leak detection equipment, thermal imaging, and acoustic listening devices to pinpoint pipe breaks without unnecessary excavation or property damage."
    },
    {
      question: "What's the difference between repair and replacement?",
      answer: "Repairs are suitable for minor breaks or leaks, while replacement is needed for severely damaged, corroded, or frequently breaking pipes. We'll assess your situation and recommend the most cost-effective solution."
    }
  ];

  return (
    <>
      <Head>
        <title>{`Pipe Leak Repair Near Me | Broken Pipe Geneva, IL | ${companyName}`}</title>
        <meta name="description" content={`Pipe leak repair near me in Geneva, St Charles & Kane County, IL. Emergency broken pipe repair, burst pipe emergency service. Fast pipe repair. Call ${phoneNumber} for immediate help!`} />
        <meta name="keywords" content="pipe leak repair near me, broken pipe emergency, burst pipe emergency Kane County, pipe repair Geneva IL, pipe leak Geneva, emergency pipe repair near me, pipe replacement Kane County" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://partnersinplumbing.com/services/broken-pipe" />
        <link rel="icon" href="/favicon.ico" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": companyName,
            "description": `Professional broken pipe repair services in ${serviceArea}`,
            "telephone": phoneNumber,
            "serviceArea": serviceArea,
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Illinois",
              "addressLocality": "Kane County",
              "postalCode": "60177"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Broken Pipe Repair Services",
              "itemListElement": brokenPipeServices.map(service => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.title,
                  "description": service.description
                }
              }))
            }
          })}
        </script>
      </Head>

      <div className="min-h-screen bg-white">
        <Header currentPage="/services/broken-pipe" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Sticky Contact Button */}
        <div className="fixed bottom-6 right-6 z-50">
          <a 
            href={`tel:${phoneNumber}`} 
            className="bg-red-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-red-700 transition-colors font-bold text-lg animate-pulse"
          >
            🚨 Emergency Call
          </a>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Emergency Broken Pipe Repair
              </h1>
              <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-3xl mx-auto">
                Fast, professional broken pipe repair services in {serviceArea}. Available for emergency situations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a 
                  href={`tel:${phoneNumber}`} 
                  className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
                >
                  Call Now: {phoneNumber}
                </a>
                <Link 
                  href="/contact" 
                  className="bg-white text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
                >
                  Get Free Quote Online
                </Link>
              </div>
              <p className="text-slate-200 text-sm">
                ⚡ Emergency service available | 💰 Free estimates | 🛡️ Licensed & insured
              </p>
            </div>
          </div>
        </section>

        {/* Emergency Signs */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Signs You Have a Broken Pipe</h2>
              <p className="text-xl text-gray-600">Call immediately if you notice any of these warning signs</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {emergencySigns.map((sign, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex-shrink-0 mr-3"></div>
                    <p className="text-gray-900 font-medium">{sign}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg text-gray-700 mb-4">Don't wait - broken pipes can cause thousands in water damage! <Link href="/services/emergency-plumbing-service" className="text-red-600 hover:text-red-800 font-semibold underline">Learn more about our emergency plumber services in Kane County</Link>.</p>
              <a href={`tel:${phoneNumber}`} className="bg-red-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors">
                Emergency Service: {phoneNumber}
              </a>
            </div>
          </div>
        </section>

        {/* Broken Pipe Services */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Broken Pipe Repair Services</h2>
              <p className="text-xl text-gray-600">Comprehensive pipe repair solutions for every situation</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {brokenPipeServices.map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link href="/contact" className="bg-yellow-500 text-slate-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors inline-block">
                    Get Free Estimate
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mid-Page Contact Section */}
        <section className="py-16 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Don't Wait - Get Help Now!</h2>
            <p className="text-xl text-blue-100 mb-8">
              Every minute counts with a broken pipe. Our emergency team is ready to respond immediately and prevent costly water damage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a 
                href={`tel:${phoneNumber}`} 
                className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                🚨 Emergency: {phoneNumber}
              </a>
              <Link 
                href="/contact" 
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                📋 Request Service Online
              </Link>
            </div>
            <p className="text-blue-200 text-sm">
              ✅ Emergency Response | ✅ Licensed & Insured | ✅ Free Damage Assessment
            </p>
          </div>
        </section>

        {/* Common Causes */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Common Causes of Broken Pipes</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Understanding what causes pipes to break can help you prevent future issues and know when to call for professional help.
                </p>
                <ul className="space-y-4">
                  {commonCauses.map((cause, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-slate-800 rounded-full flex-shrink-0 mr-3 mt-1">
                        <div className="w-2 h-2 bg-white rounded-full m-2"></div>
                      </div>
                      <span className="text-gray-700">{cause}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Prevention Tips</h3>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Insulate pipes in unheated areas
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Monitor water pressure regularly
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Schedule annual plumbing inspections
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Replace old pipes before they fail
                  </li>
                  <li className="flex items-center text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    Keep trees away from sewer lines
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Get answers to common questions about broken pipe repair</p>
            </div>
            
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Need Emergency Broken Pipe Repair?
            </h2>
            <p className="text-xl text-slate-100 mb-8 max-w-3xl mx-auto">
              Don't let a broken pipe cause expensive water damage. Our experienced plumbers are available for emergency repairs throughout {serviceArea}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`tel:${phoneNumber}`} 
                className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                Call Now: {phoneNumber}
              </a>
              <Link 
                href="/contact" 
                className="bg-white text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Service Online
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services in {serviceArea}</p>
                <p className="text-gray-300">Licensed & Insured | Emergency Service Available</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Replacement</Link></li>
                  <li><Link href="/services" className="hover:text-white">All Services</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>Phone: <a href={`tel:${phoneNumber}`} className="hover:text-white">{phoneNumber}</a></li>
                  <li>Email: <a href="mailto:info@partnersinplumbing.com" className="hover:text-white">info@partnersinplumbing.com</a></li>
                  <li><Link href="/contact" className="hover:text-white">Contact Form</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
} 